import React, { useEffect, useState, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';

import { isEmpty, map, trim } from 'lodash';
import {
  arrayOf, bool, func, number, shape, string,
} from 'prop-types';
import { TYPES } from '../../constants';
import TestSessionCardItem from './test-session-card-item';

import {
  Container,
  Header,
  Icon,
  Input,
  Link,
  Text,
} from './styles';

// When a category is default, it was created by company

const Category = (props) => {
  const inputRef = useRef(null);
  const dndRef = useRef(null);

  const {
    category,
    hideItemName,
    onAddItem,
    onMove,
    onMoveCategoryItem,
    onMoveCopy,
    onRemove,
    onRemoveCategoryItem,
    onSetCanDrop,
    onUpdate,
    sortType,
  } = props;

  const {
    id,
    name,
    projectId,
    cardSortItems,
    categoryOrder,
    isDefault,
  } = category;

  const [showInput, setShowInput] = useState(false);

  // save name on key down and blur events
  const handleSaveName = (e) => {
    const event = e;

    const newName = trim(event.target.value);
    onUpdate({ ...category, name: newName });
    setShowInput(!showInput);
    event.preventDefault();
  };

  const handleDrop = (item, monitor) => {
    const { type, cardSortCategoryId } = item;

    // check if item is a card sort item or category
    if (type === TYPES.CARD_SORT_CATEGORY) {
      onMove(item.id, categoryOrder);

      return;
    }

    if (cardSortCategoryId === id) return;

    const { index } = monitor.getDropResult() || { index: cardSortItems.length };
    onAddItem(item, category.id, index);
  };

  const handleKeyDown = (event) => {
    if (event.keyCode === 13) {
      handleSaveName(event);
    }
  };

  const [{ handlerId, isOver }, drop] = useDrop({
    accept: [TYPES.CARD_SORT_ITEM, TYPES.CARD_SORT_CATEGORY],
    drop: (item, monitor) => {
      handleDrop(item, monitor);
    },
    canDrop: (item) => item.type === TYPES.CARD_SORT_ITEM || item.type === TYPES.CARD_SORT_CATEGORY,
    collect: (monitor) => ({
      handlerId: monitor.getHandlerId(),
      isOver: !!monitor.isOver(),
    }),
    hover: (item, monitor) => {
      if (item.type !== TYPES.CARD_SORT_CATEGORY) return;

      const newItem = item;

      if (!dndRef.current) return;

      const oldIndex = newItem.index;
      const newIndex = categoryOrder;

      // Don't replace items with themselves
      if (oldIndex === newIndex) return;
      // Determine rectangle on screen
      const hoverBoundingRect = dndRef.current?.getBoundingClientRect();
      // Get vertical middle
      const hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 2;
      // Determine mouse position
      const clientOffset = monitor.getClientOffset();
      // Get pixels to the top
      const hoverClientX = clientOffset.x - hoverBoundingRect.left;
      // Dragging downwards
      if (oldIndex < newIndex && hoverClientX < hoverMiddleX) return;
      // Dragging upwards
      if (oldIndex > newIndex && hoverClientX > hoverMiddleX) return;

      onMoveCopy(item.id, categoryOrder);
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: TYPES.CARD_SORT_CATEGORY,
    item: {
      id,
      name,
      categoryOrder,
      projectId,
      isDefault,
      items: cardSortItems,
      type: TYPES.CARD_SORT_CATEGORY,
      index: categoryOrder,
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  });

  useEffect(() => {
    onSetCanDrop(!isOver);
  }, [isOver, onSetCanDrop]);

  // When it's showing the input then focus in it.
  useEffect(() => {
    if (inputRef.current && showInput) {
      inputRef.current.focus();
    }
  }, [showInput]);

  const opacity = isDragging ? 0 : 1;
  drag(drop(dndRef));

  return (
    <Container
      data-handler-id={handlerId}
      opacity={opacity}
      ref={dndRef}
    >
      <Header>
        {showInput ? (
          <Input
            ref={inputRef}
            focus
            defaultValue={name}
            onKeyDown={handleKeyDown}
            onBlur={handleSaveName}
          />
        ) : (
          <>
            {isDefault && (sortType === 'closed' || sortType === 'hybrid') ? (
              <Text>{name}</Text>
            ) : (
              <Link onClick={() => setShowInput(!showInput)}>
                {`${name || 'Click to name'}`}
              </Link>
            )}
          </>
        )}

        {!isDefault && (
          <Icon
            name="remove"
            onClick={() => onRemove(id)}
          />
        )}
      </Header>

      {!isEmpty(cardSortItems) && map(cardSortItems, (cardSortItem, index) => (
        <TestSessionCardItem
          index={index}
          key={cardSortItem.id}
          onMove={(itemId, newIndex) => onMoveCategoryItem(itemId, newIndex, cardSortItem.cardSortCategoryId)}
          onRemove={(item, didDrop) => onRemoveCategoryItem(item, id, didDrop)}
          testSessionCardItem={cardSortItem}
          hideItemName={hideItemName}
        />
      ))}
    </Container>
  );
};

Category.propTypes = {
  category: shape({
    id: string,
    name: string,
    isDefault: bool,
    projectId: string,
    categoryOrder: number,
    cardSortItems: arrayOf(
      shape({
        id: string,
        name: string,
      }),
    ),
  }).isRequired,
  hideItemName: bool.isRequired,
  onAddItem: func.isRequired,
  onMove: func.isRequired,
  onMoveCategoryItem: func.isRequired,
  onMoveCopy: func.isRequired,
  onRemove: func.isRequired,
  onRemoveCategoryItem: func.isRequired,
  onSetCanDrop: func.isRequired,
  onUpdate: func.isRequired,
  sortType: string.isRequired,
};

export default Category;
